import type { ArtRules } from '../schemas/data/art-rules.js';
import type { Rarity } from '../schemas/data/common.js';
import type { Body, Part } from '../schemas/data/visuals.js';

/**
 * Procedural vinyl-toy registry (design doc §19). Species visuals point at
 * these ids, so they're the contract species content is written against:
 * add freely, never rename or remove one that's in use.
 *
 * Sizes are relative to the body's height. Angles are in degrees: `around`
 * from the face (0) to the back (180), `up` from the bottom (-90) to the top
 * (90). Every number is a first guess to judge in the dev gallery.
 */

// TUNE: all bodies
export const BODIES: Body[] = [
  {
    id: 'blob',
    width: 1.1,
    height: 0.95,
    depth: 1,
    squareness: 0.15,
    taper: 0,
    peak: 0,
    bottomFlat: 0.45,
    jitter: 0.06,
  },
  {
    id: 'pebble',
    width: 1.25,
    height: 0.72,
    depth: 1.05,
    squareness: 0.4,
    taper: 0.05,
    peak: 0,
    bottomFlat: 0.55,
    jitter: 0.06,
  },
  {
    id: 'bean',
    width: 0.85,
    height: 1.15,
    depth: 0.82,
    squareness: 0.2,
    taper: 0,
    peak: 0,
    bottomFlat: 0.4,
    jitter: 0.05,
  },
  {
    id: 'pear',
    width: 1.05,
    height: 1.05,
    depth: 0.98,
    squareness: 0.1,
    taper: -0.3,
    peak: 0,
    bottomFlat: 0.45,
    jitter: 0.05,
  },
  {
    id: 'drop',
    width: 0.95,
    height: 1.15,
    depth: 0.95,
    squareness: 0,
    taper: 0,
    peak: 0.7,
    bottomFlat: 0.4,
    jitter: 0.05,
  },
  {
    id: 'bun',
    width: 1.3,
    height: 0.78,
    depth: 1.15,
    squareness: 0.25,
    taper: 0.08,
    peak: 0,
    bottomFlat: 0.6,
    jitter: 0.06,
  },
  {
    id: 'pumpkin',
    width: 1.3,
    height: 0.9,
    depth: 1.2,
    squareness: 0.25,
    taper: 0,
    peak: 0,
    bottomFlat: 0.5,
    lobes: { count: 8, depth: 0.08 },
    jitter: 0.05,
  },
  // ART_BIBLE §1.2: six more silhouettes.
  {
    id: 'tall',
    width: 0.74,
    height: 1.42,
    depth: 0.74,
    squareness: 0.3,
    taper: 0,
    peak: 0,
    bottomFlat: 0.6,
    jitter: 0.04,
  },
  {
    id: 'mochi',
    width: 1.45,
    height: 0.72,
    depth: 1.25,
    squareness: 0.05,
    taper: 0,
    peak: 0.45,
    bottomFlat: 0.75,
    jitter: 0.05,
  },
  {
    id: 'tiered',
    width: 1,
    height: 1.3,
    depth: 0.95,
    squareness: 0.05,
    taper: -0.22,
    peak: 0,
    bottomFlat: 0.4,
    waist: { at: 0.12, depth: 0.3, width: 0.17 },
    jitter: 0.04,
  },
  {
    // #32: a cupcake's frosting, one wide soft dome with a flat underside
    // that overhangs its paper cup (the cup is a ring of pleat legs).
    id: 'frosting',
    width: 1.3,
    height: 0.85,
    depth: 0.8,
    squareness: 0.35,
    taper: 0,
    peak: 0.18,
    bottomFlat: 0.8,
    jitter: 0.04,
  },
  {
    // A one-piece heart: round shoulders, a cleft on top and a soft point
    // at the bottom.
    id: 'heart',
    width: 1.9,
    height: 1.4,
    depth: 0.6,
    squareness: 0.25,
    taper: 0,
    peak: 0,
    bottomFlat: 0,
    cleft: { depth: 0.26, tip: 0.6 }, // TUNE: a crisp dip that still reads at a 40° turn
    jitter: 0.03,
  },
  {
    id: 'star',
    width: 1.3,
    height: 1.2,
    depth: 0.85,
    squareness: 0,
    taper: 0,
    peak: 0,
    bottomFlat: 0.2,
    points: { count: 5, depth: 0.3 },
    jitter: 0.04,
  },
  {
    // #382: a plumper, steadier five-point star than `star` (shallower points, flatter bottom),
    // for a shooting star that has to look like finished vinyl, not melted.
    id: 'comet-star',
    width: 1.25,
    height: 1.2,
    depth: 0.9,
    squareness: 0.1,
    taper: 0,
    peak: 0,
    bottomFlat: 0.35,
    points: { count: 5, depth: 0.2 },
    jitter: 0.02,
  },
  {
    id: 'ghost',
    width: 0.95,
    height: 1.3,
    depth: 0.9,
    squareness: 0.05,
    taper: -0.15,
    peak: 0.1,
    bottomFlat: 0.35,
    hem: { count: 6, depth: 0.18 },
    jitter: 0.04,
  },
  {
    // A horizontal torso for four-legged squishies.
    id: 'barrel',
    width: 0.85,
    height: 0.72,
    depth: 1.35,
    squareness: 0.15,
    taper: 0,
    peak: 0,
    bottomFlat: 0.2,
    jitter: 0.04,
  },
  {
    id: 'orb',
    width: 1.05,
    height: 1.05,
    depth: 1.05,
    squareness: 0,
    taper: 0,
    peak: 0,
    bottomFlat: 0.25,
    jitter: 0.03,
  },
];

const single = { kind: 'single' } as const;
const pair = { kind: 'pair' } as const;

// TUNE: all parts
export const PARTS: Part[] = [
  // Eyes
  {
    id: 'dot-eyes',
    slot: 'eyes',
    shape: 'ellipsoid',
    size: [0.15, 0.19, 0.08],
    color: 'ink',
    layout: pair,
    around: 26,
    up: 18,
    sink: 0.4,
    highlight: true,
    jitter: 0.05,
  },
  {
    id: 'oval-eyes',
    slot: 'eyes',
    shape: 'ellipsoid',
    size: [0.12, 0.24, 0.08],
    color: 'ink',
    layout: pair,
    around: 24,
    up: 20,
    sink: 0.4,
    highlight: true,
    jitter: 0.05,
  },
  {
    id: 'sleepy-eyes',
    slot: 'eyes',
    shape: 'arc',
    size: [0.18, 0.07, 0.055],
    color: 'ink',
    layout: pair,
    around: 26,
    up: 16,
    splay: -8,
    sink: 0.3,
    jitter: 0.04,
  },
  {
    id: 'happy-eyes',
    slot: 'eyes',
    shape: 'arc',
    size: [0.17, 0.08, 0.055],
    color: 'ink',
    layout: pair,
    around: 26,
    up: 18,
    sink: 0.3,
    flip: true,
    jitter: 0.04,
  },
  // Mouths
  {
    id: 'smile',
    slot: 'mouth',
    shape: 'arc',
    size: [0.17, 0.075, 0.05],
    color: 'ink',
    layout: single,
    around: 0,
    up: 0,
    sink: 0.3,
    jitter: 0.06,
  },
  {
    id: 'tiny-smile',
    slot: 'mouth',
    shape: 'arc',
    size: [0.1, 0.05, 0.045],
    color: 'ink',
    layout: single,
    around: 0,
    up: 2,
    sink: 0.3,
    jitter: 0.06,
  },
  {
    id: 'open-mouth',
    slot: 'mouth',
    shape: 'ellipsoid',
    size: [0.11, 0.09, 0.055],
    color: 'ink',
    layout: single,
    around: 0,
    up: -2,
    sink: 0.5,
    jitter: 0.08,
  },
  {
    id: 'cat-mouth',
    slot: 'mouth',
    shape: 'arc',
    size: [0.085, 0.045, 0.04],
    color: 'ink',
    layout: pair,
    around: 4,
    up: 0,
    sink: 0.3,
    jitter: 0.04,
  },
  // Cheeks
  {
    id: 'blush-cheeks',
    slot: 'cheeks',
    shape: 'ellipsoid',
    size: [0.16, 0.09, 0.035],
    color: 'blush',
    layout: pair,
    around: 42,
    up: 6,
    sink: 0.5,
    jitter: 0.08,
  },
  // Ears
  {
    id: 'round-ears',
    slot: 'ears',
    shape: 'ellipsoid',
    size: [0.26, 0.24, 0.13],
    color: 'primary',
    layout: pair,
    around: 40,
    up: 62,
    splay: 10,
    sink: 0.35,
    tilt: -15,
    jitter: 0.08,
  },
  {
    id: 'long-ears',
    slot: 'ears',
    shape: 'capsule',
    size: [0.16, 0.55, 0.08],
    color: 'primary',
    layout: pair,
    around: 25,
    up: 70,
    tilt: -8,
    splay: 14,
    sink: 0.25,
    jitter: 0.1,
  },
  {
    id: 'pointy-ears',
    slot: 'ears',
    shape: 'cone',
    size: [0.22, 0.3, 0.13],
    color: 'primary',
    layout: pair,
    around: 38,
    up: 60,
    splay: 16,
    sink: 0.3,
    tilt: -10,
    jitter: 0.08,
  },
  {
    id: 'floppy-ears',
    slot: 'ears',
    shape: 'capsule',
    size: [0.16, 0.42, 0.1],
    color: 'secondary',
    layout: pair,
    around: 70,
    up: 45,
    tilt: -20,
    splay: 70,
    sink: 0.3,
    jitter: 0.08,
  },
  // Horns
  {
    id: 'nub-horns',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.1, 0.12, 0.1],
    color: 'accent',
    layout: pair,
    around: 25,
    up: 70,
    splay: 10,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'cone-horns',
    slot: 'horns',
    shape: 'cone',
    size: [0.12, 0.26, 0.12],
    color: 'accent',
    layout: pair,
    around: 28,
    up: 68,
    splay: 18,
    sink: 0.25,
    jitter: 0.08,
  },
  {
    id: 'uni-horn',
    slot: 'horns',
    shape: 'cone',
    size: [0.12, 0.32, 0.12],
    color: 'accent',
    layout: single,
    around: 0,
    up: 62,
    tilt: -10,
    sink: 0.25,
    jitter: 0.08,
  },
  // Crowns
  {
    id: 'leaf-sprout',
    slot: 'crown',
    shape: 'teardrop',
    size: [0.18, 0.34, 0.06],
    color: 'secondary',
    layout: single,
    around: 0,
    up: 90,
    tilt: 20,
    sink: 0.2,
    jitter: 0.1,
  },
  {
    id: 'stem',
    slot: 'crown',
    shape: 'capsule',
    size: [0.1, 0.2, 0.1],
    color: 'accent',
    layout: single,
    around: 0,
    up: 90,
    tilt: 12,
    sink: 0.3,
    jitter: 0.1,
  },
  {
    id: 'flame-tuft',
    slot: 'crown',
    shape: 'teardrop',
    size: [0.24, 0.36, 0.24],
    color: 'accent',
    layout: single,
    around: 0,
    up: 90,
    sink: 0.35,
    jitter: 0.1,
  },
  // Tails
  {
    id: 'ball-tail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.2, 0.2, 0.2],
    color: 'secondary',
    layout: single,
    around: 180,
    up: -10,
    sink: 0.4,
    jitter: 0.1,
  },
  {
    id: 'curly-tail',
    slot: 'tail',
    shape: 'arc',
    size: [0.22, 0.22, 0.08],
    color: 'primary',
    layout: single,
    around: 180,
    up: -5,
    tilt: 20,
    sink: 0.2,
    flip: true,
    jitter: 0.1,
  },
  {
    id: 'leaf-tail',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.16, 0.3, 0.05],
    color: 'secondary',
    layout: single,
    around: 180,
    up: 0,
    tilt: 30,
    sink: 0.2,
    jitter: 0.1,
  },
  // Wings
  {
    id: 'nub-wings',
    slot: 'wings',
    shape: 'ellipsoid',
    size: [0.2, 0.3, 0.07],
    color: 'secondary',
    layout: pair,
    around: 100,
    up: 15,
    tilt: 10,
    splay: 45,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'bat-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.3, 0.45, 0.05],
    color: 'accent',
    layout: pair,
    around: 110,
    up: 25,
    tilt: 25,
    splay: 50,
    sink: 0.2,
    jitter: 0.08,
  },
  // Patterns
  {
    id: 'spots',
    slot: 'pattern',
    shape: 'ellipsoid',
    size: [0.12, 0.12, 0.025],
    color: 'secondary',
    layout: { kind: 'scatter', count: 6, aroundRange: 110, upRange: 30 },
    around: 180,
    up: 25,
    sink: 0.6,
    jitter: 0.25,
  },
  {
    id: 'belly-patch',
    slot: 'pattern',
    shape: 'ellipsoid',
    size: [0.55, 0.45, 0.04],
    color: 'secondary',
    layout: single,
    around: 0,
    up: -25,
    sink: 0.75,
    jitter: 0.05,
  },
  {
    id: 'freckles',
    slot: 'pattern',
    shape: 'ellipsoid',
    size: [0.025, 0.025, 0.012],
    color: 'detail',
    layout: { kind: 'scatter', count: 6, aroundRange: 45, upRange: 8 },
    around: 0,
    up: 6,
    sink: 0.4,
    jitter: 0.2,
  },
  // ART_BIBLE §1: feeling face kit and line signature parts.
  {
    id: 'brave-brows',
    slot: 'brows',
    shape: 'arc',
    size: [0.13, 0.045, 0.045],
    color: 'ink',
    layout: pair,
    around: 25,
    up: 36,
    splay: 18,
    sink: 0.3,
    flip: true,
    jitter: 0.04,
  },
  {
    id: 'spooky-eyes',
    slot: 'eyes',
    shape: 'ellipsoid',
    size: [0.15, 0.27, 0.08],
    color: 'ink',
    layout: pair,
    around: 22,
    up: 16,
    sink: 0.4,
    highlight: true,
    jitter: 0.05,
  },
  {
    id: 'boo-mouth',
    slot: 'mouth',
    shape: 'ellipsoid',
    size: [0.08, 0.11, 0.05],
    color: 'ink',
    layout: single,
    around: 0,
    up: -3,
    sink: 0.5,
    jitter: 0.06,
  },
  {
    id: 'water-curl',
    slot: 'crown',
    shape: 'arc',
    size: [0.26, 0.24, 0.09],
    color: 'accent',
    layout: single,
    around: 0,
    up: 84,
    tilt: 15,
    sink: 0.2,
    flip: true,
    jitter: 0.08,
  },
  {
    id: 'fin-crest',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.36, 0.44, 0.08],
    color: 'accent',
    layout: single,
    around: 180,
    up: 40,
    tilt: 25,
    sink: 0.3,
    jitter: 0.06,
  },
  {
    id: 'nightcap',
    slot: 'crown',
    shape: 'cone',
    size: [0.42, 0.62, 0.42],
    color: 'accent',
    layout: single,
    around: 0,
    up: 90,
    tilt: 40,
    sink: 0.35,
    jitter: 0.06,
  },
  {
    id: 'crag-nubs',
    slot: 'back',
    shape: 'ellipsoid',
    size: [0.2, 0.18, 0.2],
    color: 'detail',
    layout: { kind: 'scatter', count: 5, aroundRange: 70, upRange: 20 },
    around: 140,
    up: 50,
    sink: 0.4,
    jitter: 0.25,
  },
  {
    id: 'flame-crown',
    slot: 'crown',
    shape: 'teardrop',
    size: [0.24, 0.42, 0.24],
    color: 'accent',
    layout: { kind: 'scatter', count: 3, aroundRange: 50, upRange: 4 },
    around: 0,
    up: 68,
    sink: 0.35,
    jitter: 0.15,
  },
  {
    id: 'icicle-crown',
    slot: 'crown',
    shape: 'cone',
    size: [0.12, 0.3, 0.12],
    color: 'accent',
    layout: { kind: 'scatter', count: 5, aroundRange: 110, upRange: 8 },
    around: 0,
    up: 70,
    sink: 0.3,
    jitter: 0.2,
  },
  {
    id: 'bolt-ears',
    slot: 'ears',
    shape: 'cone',
    size: [0.26, 0.5, 0.13],
    color: 'primary',
    layout: pair,
    around: 32,
    up: 62,
    splay: 26,
    sink: 0.3,
    tilt: -6,
    jitter: 0.06,
  },
  {
    id: 'spark-crest',
    slot: 'crown',
    shape: 'cone',
    size: [0.14, 0.34, 0.14],
    color: 'accent',
    layout: { kind: 'scatter', count: 3, aroundRange: 30, upRange: 5 },
    around: 0,
    up: 78,
    sink: 0.3,
    jitter: 0.15,
  },
  {
    id: 'zing-antennae',
    slot: 'horns',
    shape: 'capsule',
    size: [0.07, 0.46, 0.07],
    color: 'accent',
    layout: pair,
    around: 16,
    up: 72,
    splay: 24,
    sink: 0.2,
    jitter: 0.06,
  },
  {
    id: 'bubble-crown',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [0.2, 0.2, 0.2],
    color: 'secondary',
    layout: { kind: 'scatter', count: 4, aroundRange: 100, upRange: 14 },
    around: 0,
    up: 62,
    sink: 0.35,
    jitter: 0.3,
  },
  {
    id: 'thistle-crown',
    slot: 'crown',
    shape: 'cone',
    size: [0.12, 0.32, 0.12],
    color: 'accent',
    layout: { kind: 'scatter', count: 8, aroundRange: 150, upRange: 12 },
    around: 0,
    up: 74,
    sink: 0.3,
    jitter: 0.2,
  },
  {
    id: 'leaf-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.24, 0.48, 0.05],
    color: 'primary',
    layout: pair,
    around: 100,
    up: 20,
    tilt: 25,
    splay: 50,
    sink: 0.2,
    jitter: 0.08,
  },
  {
    id: 'fluff-tail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.42, 0.4, 0.4],
    color: 'secondary',
    layout: single,
    around: 180,
    up: 5,
    sink: 0.35,
    jitter: 0.08,
  },
  {
    id: 'crystal-crown',
    slot: 'crown',
    shape: 'cone',
    size: [0.18, 0.42, 0.18],
    color: 'accent',
    layout: { kind: 'scatter', count: 3, aroundRange: 40, upRange: 6 },
    around: 0,
    up: 72,
    sink: 0.3,
    jitter: 0.2,
  },
  {
    id: 'crystal-spines',
    slot: 'back',
    shape: 'cone',
    size: [0.16, 0.36, 0.16],
    color: 'accent',
    layout: { kind: 'scatter', count: 4, aroundRange: 50, upRange: 18 },
    around: 155,
    up: 38,
    sink: 0.3,
    jitter: 0.2,
  },
  {
    id: 'muffin-cap',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [1.5, 0.5, 1.4],
    color: 'accent',
    layout: single,
    around: 0,
    up: 90,
    sink: 0.78,
    jitter: 0.04,
  },
  {
    id: 'fern-tail',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.3, 0.56, 0.06],
    color: 'accent',
    layout: single,
    around: 180,
    up: 10,
    tilt: 40,
    sink: 0.2,
    jitter: 0.08,
  },
  {
    id: 'sun-rays',
    slot: 'crown',
    shape: 'cone',
    size: [0.14, 0.26, 0.14],
    color: 'accent',
    // A halo fanned out behind the orb, so it rims the silhouette and never covers the face.
    layout: { kind: 'scatter', count: 7, aroundRange: 75, upRange: 35 },
    around: 150,
    up: 30,
    sink: 0.3,
    jitter: 0.12,
  },
  {
    id: 'glow-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.34, 0.54, 0.06],
    color: 'secondary',
    layout: pair,
    around: 105,
    up: 25,
    tilt: 30,
    splay: 55,
    sink: 0.2,
    jitter: 0.08,
  },
  {
    id: 'cloud-crown',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [0.42, 0.36, 0.38],
    color: 'secondary',
    layout: { kind: 'scatter', count: 4, aroundRange: 120, upRange: 14 },
    around: 0,
    up: 62,
    sink: 0.5,
    jitter: 0.2,
  },
  {
    id: 'bolt-tail',
    slot: 'tail',
    shape: 'cone',
    size: [0.2, 0.46, 0.12],
    color: 'accent',
    layout: single,
    around: 180,
    up: 10,
    tilt: 35,
    sink: 0.2,
    jitter: 0.08,
  },
  {
    id: 'plume-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.32, 0.6, 0.07],
    color: 'accent',
    layout: pair,
    around: 105,
    up: 25,
    tilt: 30,
    splay: 50,
    sink: 0.2,
    jitter: 0.08,
  },
  {
    id: 'big-bat-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.42, 0.62, 0.05],
    color: 'accent',
    layout: pair,
    around: 110,
    up: 25,
    tilt: 25,
    splay: 50,
    sink: 0.2,
    jitter: 0.08,
  },
  {
    id: 'whisker-flames',
    slot: 'horns',
    shape: 'teardrop',
    size: [0.14, 0.24, 0.14],
    color: 'accent',
    layout: pair,
    around: 48,
    up: 40,
    splay: 40,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'wisp-tail',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.24, 0.44, 0.24],
    color: 'secondary',
    layout: single,
    around: 180,
    up: -15,
    tilt: 30,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'leaf-ears',
    slot: 'ears',
    shape: 'teardrop',
    size: [0.22, 0.4, 0.05],
    color: 'accent',
    layout: pair,
    around: 40,
    up: 60,
    splay: 40,
    sink: 0.2,
    jitter: 0.08,
  },
  // ART_BIBLE §1.2 body plans (mockup 2): legs, arms, tails, spines, manes, fierce faces.
  {
    // Legs reach the ground on their own (their length is ignored).
    id: 'stubby-legs',
    slot: 'legs',
    shape: 'capsule',
    size: [0.3, 0.3, 0.3],
    color: 'primary',
    layout: { kind: 'quad' },
    around: 38,
    up: -45,
    splay: 6,
    sink: 0.35,
    jitter: 0.05,
  },
  {
    id: 'long-legs',
    slot: 'legs',
    shape: 'capsule',
    size: [0.27, 0.3, 0.27],
    color: 'primary',
    layout: { kind: 'quad' },
    around: 36,
    up: -40,
    splay: 4,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'biped-legs',
    slot: 'legs',
    shape: 'capsule',
    size: [0.3, 0.3, 0.32],
    color: 'primary',
    layout: pair,
    around: 30,
    up: -55,
    splay: 8,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'stubby-arms',
    slot: 'arms',
    shape: 'capsule',
    size: [0.15, 0.34, 0.15],
    color: 'primary',
    layout: pair,
    around: 78,
    up: 0,
    tilt: -45,
    splay: 25,
    sink: 0.3,
    jitter: 0.06,
  },
  {
    id: 'strong-arms',
    slot: 'arms',
    shape: 'capsule',
    size: [0.17, 0.58, 0.17],
    color: 'primary',
    layout: pair,
    around: 80,
    up: 15,
    tilt: -55,
    splay: 22,
    sink: 0.25,
    jitter: 0.05,
  },
  {
    id: 'wiggle-tail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.24, 0.28, 0.24],
    color: 'primary',
    layout: { kind: 'chain', count: 4, step: 0.8, curl: 20, wave: 15, shrink: 0.8 },
    around: 180,
    up: -25,
    tilt: 10,
    sink: 0.3,
    jitter: 0.04,
  },
  {
    id: 'serpent-body',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.5, 0.56, 0.5],
    color: 'primary',
    layout: { kind: 'chain', count: 9, step: 0.75, curl: 9, wave: 28, shrink: 0.9 },
    around: 180,
    up: -40,
    tilt: -30,
    sink: 0.35,
    jitter: 0.03,
  },
  {
    id: 'flame-tail',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.22, 0.32, 0.22],
    color: 'accent',
    layout: { kind: 'chain', count: 4, step: 0.75, curl: 22, wave: 10, shrink: 0.85 },
    around: 180,
    up: -10,
    tilt: 15,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'spike-tail',
    slot: 'tail',
    shape: 'cone',
    size: [0.22, 0.34, 0.22],
    color: 'accent',
    layout: { kind: 'chain', count: 4, step: 0.7, curl: 6, wave: 0, shrink: 0.85 },
    around: 180,
    up: 10,
    tilt: 5,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'back-spines',
    slot: 'back',
    shape: 'cone',
    size: [0.14, 0.3, 0.14],
    color: 'accent',
    layout: { kind: 'row', count: 5, to: -5 },
    around: 180,
    up: 70,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'fin-row',
    slot: 'back',
    shape: 'teardrop',
    size: [0.28, 0.3, 0.06],
    color: 'accent',
    layout: { kind: 'row', count: 3, to: 20 },
    around: 180,
    up: 75,
    tilt: 30,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'spike-coat',
    slot: 'spikes',
    shape: 'cone',
    size: [0.17, 0.3, 0.17],
    color: 'accent',
    layout: { kind: 'scatter', count: 12, aroundRange: 180, upRange: 70 },
    around: 180,
    up: 10,
    sink: 0.35,
    jitter: 0.15,
  },
  {
    id: 'fangs',
    slot: 'fangs',
    shape: 'cone',
    size: [0.05, 0.09, 0.05],
    color: 'white',
    layout: pair,
    around: 7,
    up: -10,
    tilt: -85,
    sink: 0.45,
    jitter: 0.05,
  },
  {
    id: 'beak',
    slot: 'fangs',
    shape: 'cone',
    size: [0.18, 0.24, 0.14],
    color: 'accent',
    layout: single,
    around: 0,
    up: -2,
    tilt: -25,
    sink: 0.4,
    jitter: 0.05,
  },
  {
    id: 'sharp-eyes',
    slot: 'eyes',
    shape: 'ellipsoid',
    size: [0.17, 0.12, 0.08],
    color: 'ink',
    layout: pair,
    around: 25,
    up: 20,
    splay: -14,
    sink: 0.4,
    highlight: true,
    jitter: 0.04,
  },
  {
    id: 'smirk',
    slot: 'mouth',
    shape: 'arc',
    size: [0.13, 0.05, 0.045],
    color: 'ink',
    layout: single,
    around: 6,
    up: 1,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'swept-ears',
    slot: 'ears',
    shape: 'capsule',
    size: [0.15, 0.6, 0.08],
    color: 'primary',
    layout: pair,
    around: 30,
    up: 65,
    tilt: 55,
    splay: 18,
    sink: 0.25,
    jitter: 0.06,
  },
  {
    id: 'icicle-mane',
    slot: 'mane',
    shape: 'cone',
    size: [0.18, 0.34, 0.18],
    color: 'secondary',
    layout: { kind: 'ring', count: 7, spread: 95 },
    around: 180,
    up: 0,
    tilt: 20,
    sink: 0.35,
    jitter: 0.1,
  },
  {
    id: 'flame-mane',
    slot: 'mane',
    shape: 'teardrop',
    size: [0.26, 0.42, 0.26],
    color: 'accent',
    layout: { kind: 'ring', count: 7, spread: 100 },
    around: 180,
    up: 25,
    tilt: 30,
    sink: 0.35,
    jitter: 0.1,
  },
  {
    id: 'big-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.62, 1, 0.07],
    color: 'accent',
    layout: pair,
    around: 105,
    up: 30,
    tilt: 40,
    splay: 55,
    sink: 0.2,
    jitter: 0.05,
  },
  {
    id: 'storm-crest',
    slot: 'crown',
    shape: 'teardrop',
    size: [0.14, 0.5, 0.06],
    color: 'accent',
    layout: { kind: 'ring', count: 3, spread: 22 },
    around: 180,
    up: 62,
    tilt: 55,
    sink: 0.3,
    jitter: 0.08,
  },
  {
    id: 'tail-feathers',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.2, 0.62, 0.06],
    color: 'accent',
    layout: { kind: 'ring', count: 3, spread: 28 },
    around: 180,
    up: -15,
    tilt: -5,
    sink: 0.3,
    jitter: 0.06,
  },
  {
    id: 'leaf-arms',
    slot: 'arms',
    shape: 'teardrop',
    size: [0.24, 0.6, 0.07],
    color: 'detail',
    layout: pair,
    around: 82,
    up: 10,
    tilt: -40,
    splay: 30,
    sink: 0.25,
    jitter: 0.05,
  },
  {
    id: 'fur-ruff',
    slot: 'mane',
    shape: 'ellipsoid',
    size: [0.5, 0.42, 0.46],
    color: 'secondary',
    layout: { kind: 'ring', count: 11, spread: 180 },
    around: 0,
    up: -45,
    sink: 0.58,
    jitter: 0.15,
  },
  {
    id: 'tail-fan',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.26, 0.46, 0.06],
    color: 'accent',
    layout: { kind: 'ring', count: 3, spread: 35 },
    around: 180,
    up: 0,
    tilt: 0,
    sink: 0.3,
    jitter: 0.06,
  },
  {
    id: 'side-fins',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.24, 0.36, 0.05],
    color: 'accent',
    layout: pair,
    around: 80,
    up: -10,
    tilt: -20,
    splay: 45,
    sink: 0.25,
    jitter: 0.06,
  },
  {
    id: 'hop-feet',
    slot: 'legs',
    shape: 'capsule',
    size: [0.34, 0.3, 0.42],
    color: 'secondary',
    layout: pair,
    around: 32,
    up: -60,
    splay: 6,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'antlers',
    slot: 'horns',
    shape: 'cone',
    size: [0.12, 0.44, 0.12],
    color: 'accent',
    layout: pair,
    around: 22,
    up: 72,
    splay: 32,
    tilt: 10,
    sink: 0.25,
    jitter: 0.06,
  },
  {
    id: 'vine-tail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.1, 0.2, 0.1],
    color: 'accent',
    layout: { kind: 'chain', count: 6, step: 0.85, curl: 22, wave: 20, shrink: 0.92 },
    around: 180,
    up: -10,
    tilt: 0,
    sink: 0.3,
    jitter: 0.05,
  },
  // Thanksgiving (owner decision 2026-10-06): a leaf-pile squirrel line.
  {
    id: 'acorn-cap',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [0.72, 0.4, 0.7],
    color: 'accent',
    layout: single,
    around: 0,
    up: 88,
    tilt: 8,
    sink: 0.72,
    jitter: 0.05,
  },
  {
    // A big bushy tail that sweeps up behind and curls over at the tip.
    id: 'bushy-tail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.44, 0.48, 0.4],
    color: 'accent',
    layout: { kind: 'chain', count: 5, step: 0.62, curl: 10, wave: 6, shrink: 1.04 },
    around: 180,
    up: -15,
    tilt: 62,
    sink: 0.4,
    jitter: 0.05,
  },
  {
    // A leaf stuck on its back from the last leaf-pile dive.
    id: 'stuck-leaf',
    slot: 'back',
    shape: 'teardrop',
    size: [0.26, 0.38, 0.05],
    color: 'detail',
    layout: single,
    around: 115,
    up: 30,
    tilt: 40,
    sink: 0.2,
    jitter: 0.1,
  },
  {
    // A mane of maple leaves fanned around the back of the head.
    id: 'maple-mane',
    slot: 'mane',
    shape: 'teardrop',
    size: [0.3, 0.38, 0.06],
    color: 'accent',
    layout: { kind: 'ring', count: 7, spread: 120 },
    around: 180,
    up: 25,
    tilt: 35,
    sink: 0.3,
    jitter: 0.1,
  },
  {
    id: 'wisp-chain',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.3, 0.36, 0.3],
    color: 'secondary',
    layout: { kind: 'chain', count: 4, step: 0.7, curl: 20, wave: 18, shrink: 0.8 },
    around: 180,
    up: -45,
    tilt: -25,
    sink: 0.35,
    jitter: 0.05,
  },
  {
    id: 'bolt-chain',
    slot: 'tail',
    shape: 'cone',
    size: [0.16, 0.3, 0.16],
    color: 'accent',
    layout: { kind: 'chain', count: 5, step: 0.75, curl: 12, wave: 35, shrink: 0.9 },
    around: 180,
    up: 0,
    tilt: 15,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'sun-crown',
    slot: 'mane',
    shape: 'cone',
    size: [0.14, 0.3, 0.14],
    color: 'accent',
    layout: { kind: 'ring', count: 8, spread: 180 },
    around: 0,
    up: 62,
    tilt: 20,
    sink: 0.3,
    jitter: 0.08,
  },
  // #32 redesigns: a squirrel's acorn cup and plume tail, a cupcake's
  // pleated cup and sprinkles.
  {
    // Short, pointed squirrel ears (not tall bunny ears).
    id: 'tuft-ears',
    slot: 'ears',
    shape: 'teardrop',
    size: [0.26, 0.4, 0.09],
    color: 'primary',
    layout: pair,
    around: 66,
    up: 36,
    tilt: 4,
    splay: 30,
    sink: 0.1,
    jitter: 0.05,
  },
  {
    // Soft, rounded paws (rounder than stubby-legs' little blocks).
    id: 'round-paws',
    slot: 'legs',
    shape: 'ellipsoid',
    size: [0.32, 0.3, 0.36],
    color: 'primary',
    layout: { kind: 'quad' },
    around: 38,
    up: -45,
    splay: 6,
    sink: 0.35,
    jitter: 0.05,
  },
  {
    // An acorn's cup worn as a cap.
    id: 'acorn-cup',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [0.62, 0.36, 0.6],
    color: 'detail',
    layout: single,
    around: 0,
    up: 90,
    sink: 0.6,
    jitter: 0.04,
  },
  {
    // Little scale nubs poking through the acorn cap, so it reads as an acorn cup.
    id: 'acorn-scales',
    slot: 'mane',
    shape: 'ellipsoid',
    size: [0.15, 0.33, 0.15],
    color: 'accent',
    layout: { kind: 'ring', count: 16, spread: 180 },
    around: 0,
    up: 70,
    sink: 0.36,
    tilt: 0,
    jitter: 0.1,
  },
  {
    // The acorn cap's little stem.
    id: 'acorn-stem',
    slot: 'horns',
    shape: 'capsule',
    size: [0.11, 0.5, 0.11],
    color: 'accent',
    layout: single,
    around: 0,
    up: 90,
    tilt: 12,
    sink: 0.3,
    jitter: 0.05,
  },

  {
    // A wide smile that sits above the buck teeth, so it isn't hidden by them.
    id: 'nut-smile',
    slot: 'mouth',
    shape: 'arc',
    size: [0.27, 0.11, 0.075],
    color: 'ink',
    layout: single,
    around: 0,
    up: 10,
    sink: 0.3,
    jitter: 0.04,
  },
  {
    // Two big front teeth.
    id: 'buck-teeth',
    slot: 'fangs',
    shape: 'capsule',
    size: [0.075, 0.13, 0.05],
    color: 'white',
    layout: pair,
    around: 4,
    up: 2,
    tilt: -86,
    sink: 0.4,
    jitter: 0.04,
  },
  {
    // One big smooth plume of a tail: narrow where it joins, fluffy and round at the end.
    id: 'plume-tail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.86, 1.5, 0.7],
    color: 'accent',
    layout: single,
    around: 172,
    up: 6,
    tilt: 60,
    sink: 0.4,
    jitter: 0.05,
  },
  {
    // The tail's cream tip, peeking out of the plume's end.
    id: 'plume-tip',
    slot: 'back',
    shape: 'ellipsoid',
    size: [0.62, 1.4, 0.54],
    color: 'secondary',
    layout: single,
    around: 172,
    up: 16,
    tilt: 82,
    sink: 0.2,
    jitter: 0.05,
  },

  {
    // A cupcake's paper liner: deep pleats all the way round, narrow at the
    // bottom and flaring out to the rim under the frosting.
    id: 'pleat-cup',
    slot: 'back',
    shape: 'capsule',
    size: [0.36, 0.72, 0.3],
    color: 'secondary',
    layout: { kind: 'ring', count: 16, spread: 180 },
    around: 0,
    up: -72,
    tilt: -28,
    sink: 0.3,
    jitter: 0.02,
  },

  {
    // A soft scalloped lip where the frosting overhangs its paper liner
    // (in the free tail slot: a cupcake has no tail).
    id: 'frosting-lip',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.36, 0.2, 0.26],
    color: 'primary',
    layout: { kind: 'ring', count: 16, spread: 180 },
    around: 0,
    up: -38,
    tilt: -60,
    sink: 0.55,
    jitter: 0.03,
  },
  {
    // Big white googly eye patches behind small dot eyes: a silly, wide-eyed look.
    id: 'googly-whites',
    slot: 'pattern',
    shape: 'ellipsoid',
    size: [0.26, 0.3, 0.05],
    color: 'white',
    layout: pair,
    around: 25,
    up: 18,
    sink: 0.45,
    jitter: 0.04,
  },
  {
    // A pink tongue in the laughing mouth.
    id: 'giggle-tongue',
    slot: 'cheeks',
    shape: 'ellipsoid',
    size: [0.16, 0.08, 0.05],
    color: 'blush',
    layout: single,
    around: 0,
    up: -3,
    sink: 0.2,
    jitter: 0.04,
  },
  {
    // A big, thick laughing grin; the small open-mouth sits inside its cup.
    id: 'giggle-grin',
    slot: 'brows',
    shape: 'arc',
    size: [0.44, 0.22, 0.09],
    color: 'ink',
    layout: single,
    around: 0,
    up: -1,
    sink: 0.45,
    jitter: 0.04,
  },

  {
    // A little cherry on top.
    id: 'cherry',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [0.22, 0.22, 0.22],
    color: 'accent',
    layout: single,
    around: 22,
    up: 80,
    sink: 0.2,
    tilt: 20,
    jitter: 0.04,
  },
  {
    // Yellow sprinkles, lying every which way.
    id: 'sprinkles-yellow',
    slot: 'spikes',
    shape: 'capsule',
    size: [0.15, 0.075, 0.075],
    color: 'detail',
    layout: { kind: 'scatter', count: 12, aroundRange: 180, upRange: 20 },
    around: 0,
    up: 60,
    sink: 0.4,
    jitter: 0.12,
  },
  {
    // Chocolate sprinkles, low on the sides and back only, so nothing dark
    // sits near the eyes.
    id: 'sprinkles-choc',
    slot: 'horns',
    shape: 'capsule',
    size: [0.075, 0.15, 0.075],
    color: 'ink',
    layout: { kind: 'scatter', count: 12, aroundRange: 85, upRange: 34 },
    around: 180,
    up: 20,
    tilt: 85,
    sink: 0.4,
    jitter: 0.12,
  },
  {
    // The cherry's little stem.
    id: 'cherry-stem',
    slot: 'mane',
    shape: 'capsule',
    size: [0.045, 0.36, 0.045],
    color: 'ink',
    layout: single,
    around: 22,
    up: 80,
    tilt: 30,
    sink: 0.1,
    jitter: 0.05,
  },

  // #382 wave 1: four parts so frogs, turtles and moths read at a glance.
  // All existing primitives (no client geometry code); a species' `glow` lights them
  // like any other part.
  {
    // Frog and toad eyes: two domes above the face, on the head.
    id: 'eye-bumps',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.36, 0.3, 0.36],
    color: 'primary',
    layout: pair,
    around: 42,
    up: 26,
    tilt: 10,
    splay: 10,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    // A turtle's or snail's domed shell, riding on the back.
    id: 'shell-dome',
    slot: 'back',
    shape: 'ellipsoid',
    size: [1.5, 1.0, 1.5],
    color: 'accent',
    layout: single,
    around: 180,
    up: 50,
    tilt: 0,
    sink: 0.97,
    jitter: 0.04,
  },
  {
    // Moth wings swept high and wide, so both show over the shoulders from a three-quarter view.
    id: 'moth-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.8, 1.2, 0.14],
    color: 'accent',
    layout: pair,
    around: 68,
    up: 60,
    tilt: 55,
    splay: 50,
    sink: 0.25,
    jitter: 0.04,
  },
  // #382 wave 1, second pass: a data-only handle for the turtle's shell.
  {
    // An upright C of a handle at the back, like a teacup's.
    id: 'handle-arc',
    slot: 'tail',
    shape: 'arc',
    size: [0.5, 0.5, 0.12],
    color: 'accent',
    layout: single,
    around: 180,
    up: 20,
    tilt: 80,
    sink: 0.3,
    jitter: 0.03,
  },
  {
    // Bolder happy-eye arcs laid exactly over the plain ones (same place, a
    // little bigger and thicker), so the eyes still read at night.
    id: 'bold-happy-eyes',
    slot: 'brows',
    shape: 'arc',
    size: [0.22, 0.11, 0.08],
    color: 'ink',
    layout: pair,
    around: 26,
    up: 18,
    sink: 0.3,
    flip: true,
    jitter: 0.04,
  },

  {
    // A big round open mouth.
    id: 'round-mouth',
    slot: 'mouth',
    shape: 'ellipsoid',
    size: [0.18, 0.25, 0.06],
    color: 'accent',
    layout: single,
    around: 0,
    up: -4,
    sink: 0.45,
    jitter: 0.04,
  },

  {
    // Two little round hands held out in front.
    id: 'round-hands',
    slot: 'arms',
    shape: 'ellipsoid',
    size: [0.18, 0.24, 0.18],
    color: 'primary',
    layout: pair,
    around: 78,
    up: -6,
    tilt: -15,
    sink: 0.4,
    splay: 30,
    jitter: 0.05,
  },

  {
    // Big rosy cheeks under the eyes.
    id: 'heart-blush',
    slot: 'pattern',
    shape: 'ellipsoid',
    size: [0.16, 0.1, 0.04],
    color: 'blush',
    layout: pair,
    around: 32,
    up: 6,
    sink: 0.5,
    jitter: 0.05,
  },
  // #495 grown-ups: two signature parts on existing shapes.
  // A mantle of spikes over both shoulders and the back, kept off the legs and the face.
  {
    id: 'thorn-mantle',
    slot: 'spikes',
    shape: 'cone',
    size: [0.15, 0.27, 0.15],
    color: 'accent',
    layout: { kind: 'ring', count: 9, spread: 110 },
    around: 180,
    up: 46,
    sink: 0.35,
    jitter: 0.12,
  },
  // A full ring of round petals splayed out round the top (sun-crown has pointed rays).
  {
    id: 'petal-ruff',
    slot: 'mane',
    shape: 'ellipsoid',
    size: [0.24, 0.26, 0.09],
    color: 'accent',
    layout: { kind: 'ring', count: 8, spread: 180 },
    around: 0,
    up: 60,
    tilt: 55,
    sink: 0.3,
    jitter: 0.06,
  },
  // #382 wave 2: twenty-three parts so a tusk, a snout, a curly tail, a chest bib, hug arms, a slide
  // trail, a lure, a ram horn and a stacked wing can be the silhouette instead of a small add-on. All existing
  // primitives (no client geometry code); a species' `glow` lights them like any other part.
  // A zigzag wake of ice on the ground behind a sliding penguin.
  {
    id: 'slide-trail',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.5, 0.42, 0.26],
    color: 'detail',
    layout: { kind: 'chain', count: 3, step: 1.2, curl: 0, wave: 25, shrink: 0.78 },
    around: 180,
    up: -30,
    tilt: 15,
    sink: 0.45,
    jitter: 0.04,
  },
  // An anglerfish's lure: a beaded stalk arching over the face that swells into a bulb.
  {
    id: 'lure-beads',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.17, 0.17, 0.17],
    color: 'accent',
    layout: { kind: 'chain', count: 3, step: 0.8, curl: -14, wave: 0, shrink: 1.2 },
    around: 0,
    up: 80,
    tilt: -20,
    sink: 0.3,
    jitter: 0.03,
  },
  // A pair of big pointed wings swept back along the flanks (thick enough to read from the front).
  {
    id: 'puff-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.9, 1.1, 0.25],
    color: 'accent',
    layout: pair,
    around: 95,
    up: 25,
    tilt: 20,
    splay: 50,
    sink: 0.3,
    jitter: 0.05,
  },
  // A lamb's pair of big curled ram horns, one on each side of its head.
  {
    id: 'ram-horns',
    slot: 'horns',
    shape: 'arc',
    size: [0.65, 0.65, 0.2],
    color: 'accent',
    layout: pair,
    around: 80,
    up: 30,
    tilt: 0,
    splay: 40,
    sink: 0.3,
    jitter: 0.05,
  },
  // Wide moth-style wings held out to the sides, clear of the plume tail behind them.
  {
    id: 'glim-wings',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.46, 0.7, 0.07],
    color: 'secondary',
    layout: pair,
    around: 92,
    up: 12,
    tilt: 8,
    splay: 75,
    sink: 0.2,
    jitter: 0.06,
  },
  // A firefly's glowing lobe, swept back behind it so it stays clear of the sides of the frame.
  {
    id: 'glim-lobe',
    slot: 'tail',
    shape: 'ellipsoid',
    size: [0.76, 1.32, 0.64],
    color: 'accent',
    layout: single,
    around: 165,
    up: 6,
    tilt: 58,
    sink: 0.4,
    jitter: 0.05,
  },
  // Wider moth wings for a firefly's last form, so its outline steps out again from `glim-wings`.
  {
    id: 'glim-wings-wide',
    slot: 'wings',
    shape: 'teardrop',
    size: [0.54, 0.84, 0.07],
    color: 'secondary',
    layout: pair,
    around: 92,
    up: 16,
    tilt: 8,
    splay: 62,
    sink: 0.2,
    jitter: 0.06,
  },
  // A pair of icy blue spikes flaring out from the sides of a fish's head, clear of its back fins.
  {
    id: 'icicle-fins',
    slot: 'mane',
    shape: 'cone',
    size: [0.16, 0.42, 0.16],
    color: 'secondary',
    layout: pair,
    around: 70,
    up: 34,
    tilt: 40,
    splay: 60,
    sink: 0.3,
    jitter: 0.06,
  },
  // A small smile set above the pinch of a firefly's two-ball body, so it doesn't sit on the seam.
  {
    id: 'glim-smile',
    slot: 'mouth',
    shape: 'arc',
    size: [0.14, 0.06, 0.05],
    color: 'ink',
    layout: single,
    around: 0,
    up: 4,
    sink: 0.3,
    jitter: 0.06,
  },
  // A clear white oval belly on the front of a flat penguin's body (a surface patch, so it can't read as a foot).
  {
    id: 'belly-oval',
    slot: 'pattern',
    shape: 'ellipsoid',
    size: [0.7, 0.5, 0.05],
    color: 'white',
    layout: single,
    around: 18,
    up: -6,
    sink: 0.6,
    jitter: 0.02,
  },
  // A boar's two small tusks, angled up and forward from beside the snout.
  {
    id: 'pig-tusks',
    slot: 'fangs',
    shape: 'cone',
    size: [0.1, 0.3, 0.1],
    color: 'white',
    layout: pair,
    around: 8,
    up: -24,
    tilt: 90,
    splay: 0,
    sink: 0.35,
    jitter: 0.03,
  },
  // A firefly's two round hands held out in front, in a colour that shows against its dark body.
  {
    id: 'glim-hands',
    slot: 'arms',
    shape: 'ellipsoid',
    size: [0.18, 0.24, 0.18],
    color: 'accent',
    layout: pair,
    around: 78,
    up: -6,
    tilt: -15,
    sink: 0.4,
    splay: 30,
    jitter: 0.05,
  },
  // Spikes over the back half of a dragonfruit only, so none lands on its cheek and covers the blush.
  {
    id: 'back-scales',
    slot: 'spikes',
    shape: 'cone',
    size: [0.17, 0.3, 0.17],
    color: 'accent',
    layout: { kind: 'scatter', count: 12, aroundRange: 80, upRange: 60 },
    around: 180,
    up: 20,
    sink: 0.35,
    jitter: 0.15,
  },
  // A bear's wreath of autumn leaves lying flat round the top of its head (a crown, not horns).
  {
    id: 'leaf-crown',
    slot: 'mane',
    shape: 'teardrop',
    size: [0.4, 0.44, 0.07],
    color: 'accent',
    layout: { kind: 'scatter', count: 7, aroundRange: 180, upRange: 8 },
    around: 0,
    up: 66,
    tilt: 70,
    sink: 0.3,
    jitter: 0.08,
  },
  // A pig's flat round snout disc, pushed out from the front of its head.
  {
    id: 'pig-snout',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.36, 0.2, 0.3],
    color: 'secondary',
    layout: single,
    around: 0,
    up: -14,
    sink: 0.3,
    jitter: 0.03,
  },
  // A pig's pointy triangle ears, standing up and out from the top of its head.
  {
    id: 'pig-ears',
    slot: 'ears',
    shape: 'cone',
    size: [0.3, 0.34, 0.1],
    color: 'secondary',
    layout: pair,
    around: 62,
    up: 50,
    tilt: 25,
    splay: 28,
    sink: 0.25,
    jitter: 0.05,
  },
  // Four bubbles in a tuft across the top of a pig's head.
  {
    id: 'pig-bubbles',
    slot: 'crown',
    shape: 'ellipsoid',
    size: [0.2, 0.2, 0.2],
    color: 'secondary',
    layout: { kind: 'ring', count: 4, spread: 55 },
    around: 0,
    up: 68,
    sink: 0.35,
    jitter: 0.15,
  },
  // A pig's short stub of a tail, flicked up from the rump.
  {
    id: 'pig-tail',
    slot: 'tail',
    shape: 'capsule',
    size: [0.12, 0.28, 0.12],
    color: 'accent',
    layout: single,
    around: 180,
    up: 22,
    tilt: 60,
    sink: 0.3,
    jitter: 0.03,
  },
  // A cub's big red heart held to its chest: two round lobes side by side...
  {
    id: 'heart-lobes',
    slot: 'spikes',
    shape: 'ellipsoid',
    size: [0.56, 0.38, 0.42],
    color: 'detail',
    layout: pair,
    around: 14,
    up: 4,
    splay: 0,
    sink: 0.5,
    jitter: 0.02,
  },
  // ...and the heart's point below them.
  {
    id: 'heart-point',
    slot: 'wings',
    shape: 'cone',
    size: [0.8, 0.7, 0.4],
    color: 'detail',
    layout: single,
    around: 0,
    up: -14,
    tilt: 90,
    sink: 0.5,
    jitter: 0.02,
  },
  // The cub's two arms wrapped round the heart, paws meeting in the middle.
  {
    id: 'heart-arms',
    slot: 'arms',
    shape: 'capsule',
    size: [0.28, 0.62, 0.28],
    color: 'primary',
    layout: pair,
    around: 56,
    up: 15,
    tilt: -36,
    splay: -72,
    sink: 0.1,
    jitter: 0.03,
  },
  // Second and third feather lobes of a cloud wing, stacked below the main lobe.
  {
    id: 'wing-lobe-mid',
    slot: 'arms',
    shape: 'teardrop',
    size: [0.7, 0.9, 0.22],
    color: 'accent',
    layout: pair,
    around: 95,
    up: 8,
    tilt: -20,
    splay: 60,
    sink: 0.3,
    jitter: 0.05,
  },
  {
    id: 'wing-lobe-low',
    slot: 'spikes',
    shape: 'teardrop',
    size: [0.5, 0.7, 0.2],
    color: 'accent',
    layout: pair,
    around: 98,
    up: -8,
    tilt: -50,
    splay: 60,
    sink: 0.3,
    jitter: 0.05,
  },
  // A duck's wide flat bill (the `beak` cone reads as a chick's).
  {
    id: 'duck-bill',
    slot: 'fangs',
    shape: 'ellipsoid',
    size: [0.42, 0.34, 0.2],
    color: 'accent',
    layout: single,
    around: 0,
    up: -6,
    tilt: -5,
    sink: 0.25,
    jitter: 0.03,
  },
  // Bubbles rising from a duck's bill, each bigger than the last.
  {
    id: 'bubble-stream',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.17, 0.17, 0.17],
    color: 'secondary',
    layout: { kind: 'chain', count: 3, step: 0.85, curl: 35, wave: 0, shrink: 1.2 },
    around: 170,
    up: 60,
    tilt: 72,
    sink: 0.2,
    jitter: 0.03,
  },
  {
    id: 'bubble-stream-long',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.15, 0.15, 0.15],
    color: 'secondary',
    layout: { kind: 'chain', count: 6, step: 0.8, curl: 30, wave: 0, shrink: 1.15 },
    around: 170,
    up: 60,
    tilt: 72,
    sink: 0.2,
    jitter: 0.03,
  },
  // A small crest of bubbles standing up from a duck's back.
  {
    id: 'bubble-sail',
    slot: 'back',
    shape: 'ellipsoid',
    size: [0.2, 0.2, 0.2],
    color: 'secondary',
    layout: { kind: 'chain', count: 3, step: 1.1, curl: 8, wave: 0, shrink: 0.9 },
    around: 170,
    up: 62,
    tilt: 75,
    sink: 0.25,
    jitter: 0.03,
  },
  // A white ruff of nine suds spheres round the neck, wider than the head and open at the front so the bill stays clear.
  {
    id: 'suds-ruff',
    slot: 'mane',
    shape: 'ellipsoid',
    size: [0.5, 0.5, 0.5],
    color: 'white',
    layout: { kind: 'ring', count: 9, spread: 110 },
    around: 145,
    up: -19,
    tilt: -10,
    sink: 0,
    jitter: 0.06,
  },
  // Two round paw pads of a hugging bear, meeting at the front of the heart.
  {
    id: 'heart-paws',
    slot: 'back',
    shape: 'ellipsoid',
    size: [0.24, 0.42, 0.24],
    color: 'primary',
    layout: pair,
    around: 2,
    up: 3,
    sink: 0,
    jitter: 0.02,
  },
  // Spooky eyes set closer to the middle of the face, so the far one stays inside the outline in the 3/4 view.
  {
    id: 'close-spooky-eyes',
    slot: 'eyes',
    shape: 'ellipsoid',
    size: [0.15, 0.27, 0.08],
    color: 'ink',
    layout: pair,
    around: 14,
    up: 16,
    sink: 0.4,
    highlight: true,
    jitter: 0.05,
  },
  // Happy eyes set closer to the middle of the face, so the far one stays inside a narrow head's outline in the 3/4 view.
  {
    id: 'close-happy-eyes',
    slot: 'eyes',
    shape: 'arc',
    size: [0.17, 0.08, 0.055],
    color: 'ink',
    layout: pair,
    around: 14,
    up: 18,
    sink: 0.3,
    flip: true,
    jitter: 0.04,
  },
  // Oval eyes set closer to the middle of the face, so the far one stays inside a narrow head's outline in the 3/4 view.
  {
    id: 'close-oval-eyes',
    slot: 'eyes',
    shape: 'ellipsoid',
    size: [0.12, 0.24, 0.08],
    color: 'ink',
    layout: pair,
    around: 15,
    up: 20,
    sink: 0.4,
    highlight: true,
    jitter: 0.05,
  },
  // A potato's little arms, set further forward so both show in the 3/4 view.
  {
    id: 'spud-arms',
    slot: 'arms',
    shape: 'capsule',
    size: [0.15, 0.34, 0.15],
    color: 'primary',
    layout: pair,
    around: 56,
    up: 0,
    tilt: -30,
    splay: 40,
    sink: 0.3,
    jitter: 0.06,
  },
  // A hash brown's strong arms, set further forward so both show in the 3/4 view.
  {
    id: 'spud-strong-arms',
    slot: 'arms',
    shape: 'capsule',
    size: [0.17, 0.58, 0.17],
    color: 'primary',
    layout: pair,
    around: 58,
    up: 12,
    tilt: -50,
    splay: 35,
    sink: 0.25,
    jitter: 0.05,
  },
  // A tall column of bubbles rising from the bill.
  {
    id: 'bubble-stream-tall',
    slot: 'horns',
    shape: 'ellipsoid',
    size: [0.14, 0.14, 0.14],
    color: 'secondary',
    layout: { kind: 'chain', count: 8, step: 0.7, curl: 22, wave: 0, shrink: 1.1 },
    around: 170,
    up: 60,
    tilt: 72,
    sink: 0.2,
    jitter: 0.03,
  },
  // The back of a duck's hull sweeping up into its tail point.
  {
    id: 'duck-tail',
    slot: 'tail',
    shape: 'teardrop',
    size: [0.6, 0.75, 0.55],
    color: 'primary',
    layout: single,
    around: 180,
    up: 8,
    tilt: 40,
    sink: 0.45,
    jitter: 0.03,
  },
];

/**
 * The squishy art rules (docs/ART_BIBLE.md §1), held against every species
 * by `checkGameData`.
 */
export const ART_RULES: ArtRules = {
  defaultInk: '#3b2a3f', // TUNE: soft plum, warmer than black
  minInkContrast: 4.5,
  lightnessVariation: 0.06, // TUNE: the client's per-squishy lightness wobble
  warmthVariation: 0.025, // TUNE: …and warmth wobble
  facePatchWidth: 0.3, // TUNE
  finishByRarity: {
    common: 'vinyl',
    uncommon: 'vinyl',
    rare: 'vinyl',
    epic: 'sparkle',
    legendary: 'iridescent',
    mythic: 'shimmer',
    secret: 'iridescent',
  },
  glowByElement: { light: 'body', fire: 'accent' },
  evolutionScale: { min: 1.08, max: 1.4 }, // TUNE: per step of baby → kid → teen → grown-up (#443); 1.4 lets an unconverted line still go baby → teen in one step
  feelingFaces: {
    sleepy: [['sleepy-eyes']],
    joy: [['happy-eyes', 'close-happy-eyes']],
    silly: [['dot-eyes', 'sharp-eyes'], ['open-mouth']],
    // Evolutions may grow up into narrower `sharp-eyes` (ART_BIBLE §1.1).
    cozy: [['oval-eyes', 'sharp-eyes', 'close-oval-eyes'], ['blush-cheeks']],
    brave: [['brave-brows'], ['oval-eyes', 'dot-eyes', 'sharp-eyes']],
    spooky: [['spooky-eyes', 'close-spooky-eyes']],
  },
};

/**
 * Rare finds glow (#391, the owner picked the coloured-edge look): how
 * strongly each rarity glows, 0 for none. The client draws rare and epic as
 * an outline glowing in each part's own colour, and brightens the rainbow
 * edge that legendary and up already wear. Secret kinds glow like
 * legendary. Rarity is public already, so this can ship to every client.
 */
export const RARE_GLOW_LEVEL: Readonly<Record<Rarity, 0 | 1 | 2 | 3 | 4>> = {
  common: 0,
  uncommon: 0,
  rare: 1, // TUNE
  epic: 2, // TUNE
  legendary: 3, // TUNE
  mythic: 4, // TUNE
  secret: 3, // TUNE
};
